import { z } from 'zod';

export const BOOKING_AGENT_STORAGE_KEY = 'knowmediq.bookingAgent.v1';
export const CAREGIVER_STANDARD_RATE_CENTS = 2500;
export const CAREGIVER_HEAVY_RATE_CENTS = 3000;
export const ADMIN_FEE_RATE = 0.08;

export const BOOKING_AGENT_COPY = {
  en: {
    title: 'Your guided care concierge',
    subtitle: 'A conversational intake that helps you describe the need, refine the details, and move toward the right provider and time slot.',
    journeyLabel: 'Your care journey',
    phaseLabel: 'Guided care journey',
    phaseTitles: {
      triage: 'Phase 1 - smart triage',
      intake: 'Phase 2 - care details',
      providers: 'Phase 3 - matched providers',
      schedule: 'Phase 4 - scheduling',
      summary: 'Phase 4 - confirmation'
    },
    stepLabels: {
      triage: 'Needs',
      intake: 'Details',
      providers: 'Matching',
      schedule: 'Confirmation',
      summary: 'Confirmation'
    },
    assistantName: 'KnowMediQ Concierge',
    assistantStatus: 'Online - replies in under 2 seconds',
    assistantGreeting: 'Hello. I am your KnowMediQ care concierge. Tell me what is going on and I will guide the next steps.',
    freeTextLabel: 'What do you need help with?',
    freeTextPlaceholder: 'Examples: I need a caregiver for my father after surgery, I need a blood draw at home, I want to speak to a nutritionist.',
    routeLabel: 'Choose a starting point',
    routeOptions: {
      caregiver: 'Caregiver',
      nurse: 'Nurse or auxiliary nurse',
      other: 'Other provider'
    },
    caregiverTierTitle: 'Caregiver support level',
    caregiverTierSubtitle: 'We need to determine whether standard support or heavy-care support is required before we continue.',
    intakeTitle: 'Intake details',
    providerTitle: 'Eligible providers',
    scheduleTitle: 'Pick a provider and time',
    summaryTitle: 'Booking summary',
    accountTitle: 'Account and payment',
    planTitle: 'Your care plan summary',
    planSubtitle: 'Review the provider, service details, timing, and transparent pricing before checkout.',
    submitRoute: 'Continue',
    back: 'Back',
    save: 'Save progress',
    providersEmpty: 'No verified active providers currently match this request.',
    next: 'Continue',
    selectProvider: 'Select provider',
    selectSlot: 'Select a time slot',
    continueGuest: 'Continue as guest',
    signIn: 'Sign in / create account',
    proceedPayment: 'Proceed to payment',
    saved: 'Progress saved on this device.',
    conciergeHint: 'You can refine the details at every step before booking.',
    quickRepliesLabel: 'Quick starts',
    quickReplies: ['Wound care at home', 'Foot care for senior', 'Mental health support', 'Nutrition consultation'],
    cancellationNote: 'Cancellation note: final cancellation terms are shown at checkout and may vary by provider/service.',
    providerTypeLabels: {
      caregiver_standard: 'Standard caregiver',
      caregiver_heavy: 'Heavy-care caregiver',
      nurse: 'Nurse / auxiliary nurse',
      other: 'Other provider'
    },
    nurseServiceLabels: {
      blood_draw: 'Blood draw',
      injection: 'Injection',
      wound_care: 'Wound care / dressing change',
      ear_wash: 'Ear wash',
      other_nursing_support: 'Other nursing support'
    },
    otherServiceLabels: {
      nutrition: 'Nutritionist',
      therapy: 'Therapist / mental health',
      foot_care: 'Foot care',
      physiotherapy: 'Physiotherapist',
      other: 'Other provider category'
    },
    caregiverQuestions: {
      autonomy: 'Autonomy',
      mobility: 'Mobility',
      liftingRequired: 'Lifting required',
      transfersRequired: 'Transfers required',
      repositioningRequired: 'Repositioning required',
      heavyHygieneSupport: 'Heavy hygiene support'
    },
    caregiverAutonomyOptions: {
      autonomous: 'Autonomous',
      partial_support: 'Needs support',
      non_autonomous: 'Non-autonomous'
    },
    caregiverMobilityOptions: {
      independent: 'Independent',
      assisted: 'Assisted',
      bedbound: 'Bedbound'
    }
  },
  fr: {
    title: 'Votre concierge de soins guidé',
    subtitle: 'Un intake conversationnel pour décrire le besoin, préciser les détails et avancer vers le bon prestataire et le bon créneau.',
    journeyLabel: 'Votre parcours de soins',
    phaseLabel: 'Parcours guidé',
    phaseTitles: {
      triage: 'Phase 1 - triage intelligent',
      intake: 'Phase 2 - plan de soins',
      providers: 'Phase 3 - correspondances',
      schedule: 'Phase 4 - réservation',
      summary: 'Phase 4 - confirmation'
    },
    stepLabels: {
      triage: 'Besoins',
      intake: 'Détails',
      providers: 'Matching',
      schedule: 'Confirmation',
      summary: 'Confirmation'
    },
    assistantName: 'Concierge KnowMediQ',
    assistantStatus: 'En ligne - répond en moins de 2 secondes',
    assistantGreeting: 'Bonjour. Je suis votre concierge KnowMediQ. Décrivez le besoin et je vous guiderai vers la suite.',
    freeTextLabel: 'De quelle aide avez-vous besoin ?',
    freeTextPlaceholder: 'Exemples : j’ai besoin d’un aidant pour mon pere apres une chirurgie, j’ai besoin d’une prise de sang a domicile, je veux parler a une nutritionniste.',
    routeLabel: 'Choisissez un point de depart',
    routeOptions: {
      caregiver: 'Aidant',
      nurse: 'Infirmiere ou infirmiere auxiliaire',
      other: 'Autre prestataire'
    },
    caregiverTierTitle: 'Niveau de soutien aidant',
    caregiverTierSubtitle: 'Nous devons confirmer s’il s’agit d’un soutien standard ou de soins lourds avant de continuer.',
    intakeTitle: 'Renseignements',
    providerTitle: 'Prestataires admissibles',
    scheduleTitle: 'Choisir un prestataire et un horaire',
    summaryTitle: 'Resume de la reservation',
    accountTitle: 'Compte et paiement',
    planTitle: 'Résumé de votre plan de soins',
    planSubtitle: 'Révisez le prestataire, le service, l’horaire et les coûts avant de passer au paiement.',
    submitRoute: 'Continuer',
    back: 'Retour',
    save: 'Sauvegarder',
    providersEmpty: 'Aucun prestataire verifie et actif ne correspond a cette demande pour le moment.',
    next: 'Continuer',
    selectProvider: 'Choisir ce prestataire',
    selectSlot: 'Choisir un creneau',
    continueGuest: 'Continuer comme invite',
    signIn: 'Connexion / creation de compte',
    proceedPayment: 'Passer au paiement',
    saved: 'La progression a ete sauvegardee sur cet appareil.',
    conciergeHint: 'Vous pouvez ajuster les détails à chaque étape avant la réservation.',
    quickRepliesLabel: 'Suggestions rapides',
    quickReplies: ['Soins de plaie à domicile', 'Soins des pieds', 'Soutien en santé mentale', 'Consultation nutrition'],
    cancellationNote: 'Note d’annulation : les conditions finales sont affichees au paiement et peuvent varier selon le prestataire ou le service.',
    providerTypeLabels: {
      caregiver_standard: 'Aidant standard',
      caregiver_heavy: 'Aidant soins lourds',
      nurse: 'Infirmiere / infirmiere auxiliaire',
      other: 'Autre prestataire'
    },
    nurseServiceLabels: {
      blood_draw: 'Prelevement sanguin',
      injection: 'Injection',
      wound_care: 'Soins de plaie / changement de pansement',
      ear_wash: 'Lavage d’oreille',
      other_nursing_support: 'Autre soutien infirmier'
    },
    otherServiceLabels: {
      nutrition: 'Nutritionniste',
      therapy: 'Therapeute / sante mentale',
      foot_care: 'Soins des pieds',
      physiotherapy: 'Physiotherapie',
      other: 'Autre categorie'
    },
    caregiverQuestions: {
      autonomy: 'Autonomie',
      mobility: 'Mobilité',
      liftingRequired: 'Levage requis',
      transfersRequired: 'Transferts requis',
      repositioningRequired: 'Repositionnement requis',
      heavyHygieneSupport: 'Soutien d’hygiène lourde'
    },
    caregiverAutonomyOptions: {
      autonomous: 'Autonome',
      partial_support: 'Besoin de soutien',
      non_autonomous: 'Non autonome'
    },
    caregiverMobilityOptions: {
      independent: 'Indépendante',
      assisted: 'Assistée',
      bedbound: 'Alitée'
    }
  }
};

const baseSchema = {
  beneficiaryName: z.string().min(2),
  contactName: z.string().min(2),
  email: z.string().email(),
  phone: z.string().min(7),
  postalCode: z.string().min(3),
  preferredLanguage: z.string().min(2),
  scheduleDate: z.string().min(1),
  notes: z.string().optional().or(z.literal('')),
  preferences: z.string().optional().or(z.literal(''))
};

export const caregiverTierSchema = z.object({
  autonomy: z.enum(['autonomous', 'partial_support', 'non_autonomous']),
  mobility: z.enum(['independent', 'assisted', 'bedbound']),
  liftingRequired: z.boolean(),
  transfersRequired: z.boolean(),
  repositioningRequired: z.boolean(),
  heavyHygieneSupport: z.boolean()
});

export const intakeSchemas = {
  caregiver: z.object({
    ...baseSchema,
    requestedServices: z.array(z.string()).min(1),
    healthSafetyNotes: z.string().min(3),
    emergencySupportDetails: z.string().min(3)
  }),
  nurse: z.object({
    ...baseSchema,
    nurseServiceType: z.enum(['blood_draw', 'injection', 'wound_care', 'ear_wash', 'other_nursing_support']),
    serviceReason: z.string().min(3),
    symptomsGoals: z.string().min(3)
  }),
  other: z.object({
    ...baseSchema,
    otherServiceCategory: z.enum(['nutrition', 'therapy', 'foot_care', 'physiotherapy', 'other']),
    consultationReason: z.string().min(3),
    symptomsGoals: z.string().min(3),
    searchDuration: z.string().min(1),
    uploadReference: z.string().optional().or(z.literal(''))
  })
};

export const intakeFieldConfig = {
  caregiver: [
    { name: 'beneficiaryName', type: 'text', label: { en: 'Beneficiary name', fr: 'Nom du beneficiaire' }, required: true },
    { name: 'contactName', type: 'text', label: { en: 'Contact name', fr: 'Nom du contact' }, required: true },
    { name: 'email', type: 'email', label: { en: 'Email', fr: 'Courriel' }, required: true },
    { name: 'phone', type: 'tel', label: { en: 'Phone', fr: 'Telephone' }, required: true },
    { name: 'postalCode', type: 'text', label: { en: 'Postal code', fr: 'Code postal' }, required: true },
    { name: 'preferredLanguage', type: 'text', label: { en: 'Preferred language', fr: 'Langue preferee' }, required: true },
    { name: 'requestedServices', type: 'multiselect', label: { en: 'Requested services', fr: 'Services demandes' }, options: ['Companionship', 'Meal prep', 'Medication reminders', 'Hygiene support', 'Mobility support'], required: true },
    { name: 'healthSafetyNotes', type: 'textarea', label: { en: 'Health and safety notes', fr: 'Notes sante et securite' }, required: true },
    { name: 'scheduleDate', type: 'date', label: { en: 'Preferred date', fr: 'Date souhaitee' }, required: true },
    { name: 'preferences', type: 'textarea', label: { en: 'Provider preferences', fr: 'Preferences de prestataire' }, required: false },
    { name: 'emergencySupportDetails', type: 'textarea', label: { en: 'Emergency / support details', fr: 'Details urgence / soutien' }, required: true },
    { name: 'notes', type: 'textarea', label: { en: 'Additional notes', fr: 'Notes additionnelles' }, required: false }
  ],
  nurse: [
    { name: 'beneficiaryName', type: 'text', label: { en: 'Beneficiary name', fr: 'Nom du beneficiaire' }, required: true },
    { name: 'contactName', type: 'text', label: { en: 'Contact name', fr: 'Nom du contact' }, required: true },
    { name: 'email', type: 'email', label: { en: 'Email', fr: 'Courriel' }, required: true },
    { name: 'phone', type: 'tel', label: { en: 'Phone', fr: 'Telephone' }, required: true },
    { name: 'postalCode', type: 'text', label: { en: 'Postal code', fr: 'Code postal' }, required: true },
    { name: 'preferredLanguage', type: 'text', label: { en: 'Preferred language', fr: 'Langue preferee' }, required: true },
    { name: 'nurseServiceType', type: 'select', label: { en: 'Service type', fr: 'Type de service' }, options: ['blood_draw', 'injection', 'wound_care', 'ear_wash', 'other_nursing_support'], required: true },
    { name: 'serviceReason', type: 'textarea', label: { en: 'Reason for service', fr: 'Raison du service' }, required: true },
    { name: 'symptomsGoals', type: 'textarea', label: { en: 'Clinical details', fr: 'Details cliniques' }, required: true },
    { name: 'scheduleDate', type: 'date', label: { en: 'Preferred date', fr: 'Date souhaitee' }, required: true },
    { name: 'notes', type: 'textarea', label: { en: 'Additional notes', fr: 'Notes additionnelles' }, required: false }
  ],
  other: [
    { name: 'beneficiaryName', type: 'text', label: { en: 'Beneficiary name', fr: 'Nom du beneficiaire' }, required: true },
    { name: 'contactName', type: 'text', label: { en: 'Contact name', fr: 'Nom du contact' }, required: true },
    { name: 'email', type: 'email', label: { en: 'Email', fr: 'Courriel' }, required: true },
    { name: 'phone', type: 'tel', label: { en: 'Phone', fr: 'Telephone' }, required: true },
    { name: 'postalCode', type: 'text', label: { en: 'Location / postal code', fr: 'Lieu / code postal' }, required: true },
    { name: 'preferredLanguage', type: 'text', label: { en: 'Preferred language', fr: 'Langue preferee' }, required: true },
    { name: 'otherServiceCategory', type: 'select', label: { en: 'Provider category', fr: 'Categorie de prestataire' }, options: ['nutrition', 'therapy', 'foot_care', 'physiotherapy', 'other'], required: true },
    { name: 'consultationReason', type: 'textarea', label: { en: 'Reason for consultation', fr: 'Raison de consultation' }, required: true },
    { name: 'symptomsGoals', type: 'textarea', label: { en: 'Symptoms or goals', fr: 'Symptomes ou objectifs' }, required: true },
    { name: 'scheduleDate', type: 'date', label: { en: 'Preferred date', fr: 'Date souhaitee' }, required: true },
    { name: 'uploadReference', type: 'text', label: { en: 'Relevant document reference', fr: 'Reference de document pertinent' }, required: false },
    { name: 'searchDuration', type: 'text', label: { en: 'How long have you been looking for help?', fr: 'Depuis combien de temps cherchez-vous de l’aide ?' }, required: true },
    { name: 'notes', type: 'textarea', label: { en: 'Additional notes', fr: 'Notes additionnelles' }, required: false }
  ]
};
